import React, { Component } from "react";
import {
  toFixed,
  GET,
  loading,
  Navbar,
} from "../../components/CustomComponent.js";
import { GATEWAY_LOGO, gateway_logo_background } from "../../components/GatewayLogo.js";

/*
  Money Flow Map
  --------------
  เส้นทางเงิน 5 ชั้น (ซ้าย -> ขวา)
    Customer -> Broker -> ZenithLink Hub -> Gateway (ที่ตั้งให้ broker) -> Bank ของ customer
  วาดด้วย SVG ล้วน ไม่ใช้ library เพิ่ม
    - เส้นเชื่อมแบบหักมุม (orthogonal) วางสลับระยะกันเหมือนแผนผังเครือข่าย
    - อนุภาคแสงวิ่งตามเส้นด้วย <animateMotion> + <mpath>
        deposit    = วิ่งจากซ้ายไปขวา (customer -> ... -> bank)
        withdrawal = วิ่งย้อนกลับ (keyPoints 1 -> 0)
    - จำนวน/ความเร็วอนุภาคและความหนาเส้น แปรตามยอดของเส้นนั้น (จำกัดจำนวนรวมต่อหน้า)
    - hover โหนดใด จะไฮไลต์เฉพาะ "เส้นทางของ customer ที่ผ่านโหนดนั้น" ทั้ง 5 ชั้น
    - customer แสดงเฉพาะคนที่ยอดสูงสุดของแต่ละ broker 1 คน (พอให้เห็น flow ครบ)
    - broker แสดง top 10 ตามยอด + broker ทดสอบ (Zenith Market) ติดเสมอ
    - คอลัมน์ Gateway และ Bank แสดง "ทุกตัวที่เปิดใช้งาน" ของสกุลเงินนั้น
      ตัวที่ไม่มี flow วิ่งเข้า = โหนด idle; เส้น gateway -> bank จาก mapping จะมี effect เมื่อธนาคารมียอด
  Performance
    - อนุภาคไม่ใช้ SVG filter (วงกลม 2 ชั้นแทนแสงฟุ้ง) และจำกัดจำนวนรวมไม่เกิน MAX_PARTICLES
    - กราฟ (BuildGraph) และ hex พื้นหลัง cache ไว้ คำนวณใหม่เฉพาะเมื่อข้อมูล/filter เปลี่ยน
    - เมาส์ขยับบนโหนดย้าย tooltip ผ่าน ref โดยตรง ไม่ setState จึงไม่ re-render SVG ทั้งภาพ
  แหล่งข้อมูล
    - v1/admin/flow-map : endpoint เดียว (module แยกสำหรับหน้านี้) คืน gateways / banks / brokers / customers
  ถ้าไม่มีข้อมูลจะสลับไปใช้ชุดตัวอย่าง (แสดงป้าย "Sample data") เพื่อให้เห็นอนิเมชั่นได้ทันที
*/

const MAX_BROKERS = 10; // broker ยอดสูงสุด 10 ราย (ที่เปิดใช้งานในสกุลเงินนั้น) และ customer ตัวอย่าง broker ละ 1 คน
// broker ทดสอบที่ให้ติดเสมอแม้ยอดไม่ถึง top 10 (ส่งไปให้ backend ค้นจากชื่อ/รหัส; sample ใช้ pattern เดียวกัน)
const PINNED_BROKER = "zenith market";
const PINNED_BROKER_PATTERN = /zenith\s*[-_]?\s*market/i;
const isPinnedBroker = (b) => PINNED_BROKER_PATTERN.test(`${b.name || ""} ${b.code || ""}`);
const MAX_PARTICLES = 48; // อนุภาครวมทั้งภาพ (แต่ละเม็ดคือ animateMotion ที่วิ่งตลอดเวลา)

const FLOW_COLORS = [
  "#a78bfa", "#2dd4bf", "#fbbf24", "#38bdf8", "#f472b6",
  "#a3e635", "#fb7185", "#818cf8", "#34d399", "#f97316",
];
const IDLE_COLOR = "#475569";
const LINK_COLOR = "#7c8fd6"; // เส้น gateway -> bank จาก mapping เมื่อทั้งสองปลาย idle
const DEPOSIT_PARTICLE = "#fff7d6";
const WITHDRAWAL_PARTICLE = "#fb7185";
const HUB_COLOR = "#d4af37";

// พื้นที่วาด (viewBox) – ความสูงยืดตามจำนวนโหนดในคอลัมน์ที่ยาวที่สุด
const VB_W = 1600;
const ROW_GAP = 48;   // คอลัมน์ที่ label อยู่ข้างโหนด (customer / bank)
const STACK_GAP = 74; // คอลัมน์ที่ label อยู่บน-ล่างโหนด (broker / gateway) ต้องห่างกว่าไม่ให้ซ้อน
const MIN_H = 620;
const COL_X = { customer: 200, broker: 490, hub: 800, gateway: 1110, bank: 1400 };
const NODE_R = 22;

const num = (v) => Number(v) || 0;
const compact = (v) => {
  const n = num(v);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return toFixed(n, 0);
};
const ellipsis = (s, max = 14) => (String(s || "").length > max ? String(s).slice(0, max - 1).trimEnd() + "…" : String(s || ""));
const titleCase = (s) => String(s || "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ---------- sample data (รูปแบบเดียวกับ response ของ v1/admin/flow-map) ----------
const SAMPLE_GATEWAYS = ["chillpay", "scb", "spark", "payso", "uniqpay", "rabbit", "cimb", "reservepay", "xtc"].map((key) => ({
  key, name: titleCase(key), code: key.toUpperCase(), is_deposit_active: true, is_withdrawal_active: key !== "rabbit",
}));
const SAMPLE_BANK_LINKS = {
  KBANK: ["chillpay", "scb", "spark", "payso"], SCB: ["chillpay", "scb", "spark", "payso", "uniqpay"], BBL: ["chillpay", "spark"],
  KTB: ["chillpay", "scb", "spark"], TTB: ["payso", "chillpay"], BAY: ["payso", "spark"], GSB: ["chillpay"], KKP: ["scb"],
  UOB: ["uniqpay"], CIMB: ["cimb", "chillpay"], TISCO: ["spark"], LHB: ["reservepay"],
};
const SAMPLE_BANKS = Object.keys(SAMPLE_BANK_LINKS).map((code) => ({ key: code, code, name: code, gateways: SAMPLE_BANK_LINKS[code] }));
const SAMPLE_BROKERS = [
  { key: "b1", name: "Alpha Capital", code: "ALPHA", gateway_deposits: "chillpay", gateway_withdrawals: "chillpay", deposit: 15_800_000, withdrawal: 7_600_000 },
  { key: "b2", name: "Nova Markets", code: "NOVA", gateway_deposits: "scb", gateway_withdrawals: "spark", deposit: 11_200_000, withdrawal: 5_100_000 },
  { key: "b3", name: "Orion FX", code: "ORION", gateway_deposits: "spark", gateway_withdrawals: "spark", deposit: 8_400_000, withdrawal: 4_900_000 },
  { key: "b4", name: "Zenith Trade", code: "ZTRADE", gateway_deposits: "payso", gateway_withdrawals: "chillpay", deposit: 6_100_000, withdrawal: 2_300_000 },
  { key: "b5", name: "Helios Global", code: "HELIOS", gateway_deposits: "chillpay", gateway_withdrawals: "payso", deposit: 4_300_000, withdrawal: 2_100_000 },
  // broker ที่เปิดใช้งานแต่ยังไม่มีลูกค้า/ยอด -> idle
  { key: "b6", name: "Lumen Securities", code: "LUMEN", gateway_deposits: "uniqpay", gateway_withdrawals: "uniqpay", deposit: 0, withdrawal: 0 },
  { key: "b7", name: "Vega Partners", code: "VEGA", gateway_deposits: "scb", gateway_withdrawals: "scb", deposit: 0, withdrawal: 0 },
  // broker ทดสอบ (ติดเสมอ)
  { key: "b8", name: "Zenith Market", code: "ZMARKET", gateway_deposits: "chillpay", gateway_withdrawals: "chillpay", deposit: 120_000, withdrawal: 40_000 },
];
const SAMPLE_CUSTOMERS = [
  { key: "c1", name: "Somchai P.", broker_key: "b1", bank_code: "KBANK", deposit: 4_250_000, withdrawal: 1_900_000 },
  { key: "c2", name: "Natthaya Kittipongsakul", broker_key: "b2", bank_code: "SCB", deposit: 3_600_000, withdrawal: 1_200_000 },
  { key: "c3", name: "Wichai T.", broker_key: "b1", bank_code: "BBL", deposit: 2_900_000, withdrawal: 2_100_000 },
  { key: "c4", name: "Pimchanok S.", broker_key: "b3", bank_code: "KTB", deposit: 2_400_000, withdrawal: 600_000 },
  { key: "c5", name: "Anan R.", broker_key: "b4", bank_code: "TTB", deposit: 1_800_000, withdrawal: 900_000 },
  { key: "c6", name: "Kanya M.", broker_key: "b2", bank_code: "KBANK", deposit: 1_500_000, withdrawal: 400_000 },
  { key: "c7", name: "Thanawat L.", broker_key: "b5", bank_code: "BAY", deposit: 1_100_000, withdrawal: 750_000 },
  { key: "c8", name: "Suda C.", broker_key: "b3", bank_code: "SCB", deposit: 850_000, withdrawal: 0 },
  { key: "c9", name: "Test Account", broker_key: "b8", bank_code: "KBANK", deposit: 120_000, withdrawal: 40_000 },
];

// ---------- hex background ----------
const seeded = (seed) => () => {
  seed = (seed * 9301 + 49297) % 233280;
  return seed / 233280;
};
const HEX_R = 11;
const HEX_DX = HEX_R * Math.sqrt(3);
const HEX_DY = HEX_R * 1.5;
const hexPoints = (cx, cy, r) =>
  Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 30);
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
const glowHexCache = new Map(); // height -> cells (คำนวณครั้งเดียวต่อความสูงเวที)
const buildGlowHexes = (height) => {
  if (glowHexCache.has(height)) return glowHexCache.get(height);
  const rand = seeded(20240917);
  const cols = Math.ceil(VB_W / HEX_DX) + 1;
  const rows = Math.ceil(height / HEX_DY) + 1;
  const out = [];
  const seen = new Set(); // กลุ่มสุ่มอาจซ้อนทับกัน -> เซลล์เดียวกันซ้ำ ทำให้ key ซ้ำ
  for (let g = 0; g < 6; g++) {
    const gc = Math.floor(rand() * cols);
    const gr = Math.floor(rand() * rows);
    const size = 5 + Math.floor(rand() * 6);
    for (let k = 0; k < size; k++) {
      const c = gc + Math.floor(rand() * 5) - 2;
      const r = gr + Math.floor(rand() * 5) - 2;
      if (c < 0 || r < 0 || c >= cols || r >= rows || seen.has(`${c}-${r}`)) continue;
      seen.add(`${c}-${r}`);
      out.push({
        id: `${c}-${r}`,
        points: hexPoints(c * HEX_DX + (r % 2 ? HEX_DX / 2 : 0), r * HEX_DY, HEX_R - 1.5),
        color: FLOW_COLORS[(g * 3 + k) % FLOW_COLORS.length],
        delay: (rand() * 6).toFixed(2),
        dur: (3 + rand() * 4).toFixed(2),
        alpha: 0.25 + rand() * 0.45,
      });
    }
  }
  glowHexCache.set(height, out);
  return out;
};

// เส้นหักมุม: ออกแนวนอน -> เลี้ยวที่ bendX -> เข้าปลายทางแนวนอน (มุมโค้งเล็กน้อยให้ particle วิ่งลื่น)
const elbowPath = (x1, y1, x2, y2, bendX, radius = 10) => {
  if (Math.abs(y1 - y2) < radius * 2) return `M ${x1} ${y1} L ${x2} ${y2}`;
  const dirX = x2 > x1 ? 1 : -1;
  const dirY = y2 > y1 ? 1 : -1;
  const r = radius;
  return [
    `M ${x1} ${y1}`,
    `L ${bendX - dirX * r} ${y1}`,
    `Q ${bendX} ${y1} ${bendX} ${y1 + dirY * r}`,
    `L ${bendX} ${y2 - dirY * r}`,
    `Q ${bendX} ${y2} ${bendX + dirX * r} ${y2}`,
    `L ${x2} ${y2}`,
  ].join(" ");
};
const spreadY = (count, index, height) => {
  const top = 90, bottom = height - 70;
  return count <= 1 ? (top + bottom) / 2 : top + ((bottom - top) * index) / (count - 1);
};

const bankOf = (code) => global.bank?.find((b) => b.value === code) || null;
const bankIconCache = new Map();
const bankIcon = (code) => {
  if (bankIconCache.has(code)) return bankIconCache.get(code);
  const icon = bankOf(code)?.icon || "bank.png";
  let src;
  try {
    src = require(`../../assets/images/bank/${icon}`);
  } catch (e) {
    src = require("../../assets/images/bank/bank.png");
  }
  bankIconCache.set(code, src);
  return src;
};

export default class FlowMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter_currency: "THB",
      flow_side: "all", // all | deposit | withdrawal
      gateways: [],
      banks: [],
      brokers: [],
      customers: [],
      is_sample: false,
      loaded: false,
      hover: null, // node id เช่น "customer:c1"
      pinned: null,
      tooltip_node: null, // โหนดที่ tooltip แสดงอยู่ (ตำแหน่งย้ายผ่าน ref ไม่ผ่าน state)
      animate: true,
    };
    this.stageRef = React.createRef();
    this.tooltipRef = React.createRef();
    this.graphCache = null; // { inputs, graph }
  }

  async componentDidMount() {
    loading(false);
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      this.setState({ animate: false });
    }
    await this.Get();
  }

  Get = async () => {
    const { filter_currency } = this.state;
    loading(true);
    const result = await GET("v1/admin/flow-map", {
      currency: filter_currency,
      limit: MAX_BROKERS,
      pin_broker: PINNED_BROKER,
    });

    const data = result?.status ? result.data || {} : {};
    let gateways = (data.gateways || []).filter((g) => g.key);
    let banks = (data.banks || []).filter((b) => b.key);
    let brokers = (data.brokers || []).map((b) => ({ ...b, deposit: num(b.deposit), withdrawal: num(b.withdrawal) }));
    let customers = (data.customers || [])
      .map((c) => ({ ...c, bank_code: String(c.bank_code || "").toUpperCase(), deposit: num(c.deposit), withdrawal: num(c.withdrawal) }))
      .filter((c) => c.broker_key && c.deposit + c.withdrawal > 0);

    const is_sample = brokers.length === 0 || customers.length === 0;
    if (is_sample) {
      gateways = SAMPLE_GATEWAYS;
      banks = SAMPLE_BANKS;
      brokers = SAMPLE_BROKERS;
      customers = SAMPLE_CUSTOMERS;
    }
    this.setState({ gateways, banks, brokers, customers, is_sample, loaded: true, hover: null, pinned: null, tooltip_node: null });
    loading(false);
  };

  // ---------- graph (cache ตาม reference ของข้อมูล + flow_side) ----------
  Graph = () => {
    const { gateways, banks, brokers, customers, flow_side } = this.state;
    const c = this.graphCache;
    if (c && c.gateways === gateways && c.banks === banks && c.brokers === brokers && c.customers === customers && c.flow_side === flow_side) {
      return c.graph;
    }
    const graph = this.BuildGraph();
    this.graphCache = { gateways, banks, brokers, customers, flow_side, graph };
    return graph;
  };

  BuildGraph = () => {
    const { gateways, banks, brokers, customers, flow_side } = this.state;
    const sideAmt = (d, w) => (flow_side === "deposit" ? d : flow_side === "withdrawal" ? w : d + w);
    const brokerMap = Object.fromEntries(brokers.map((b) => [b.key, b]));

    // 1) broker ที่จะแสดง (API คัด top 10 + broker ทดสอบมาแล้ว; sample คัดตรงนี้) เรียงตามยอดของฝั่งที่เลือก
    const ranked = brokers
      .map((b) => ({ ...b, volume: sideAmt(b.deposit, b.withdrawal) }))
      .sort((a, b) => b.volume - a.volume || a.name.localeCompare(b.name));
    const bs = ranked.slice(0, MAX_BROKERS);
    ranked.slice(MAX_BROKERS).filter((b) => b.is_pinned || isPinnedBroker(b)).forEach((b) => bs.push(b));
    const bKeys = new Set(bs.map((b) => b.key));

    // 2) customer ตัวอย่าง: คนที่ยอดสูงสุดของแต่ละ broker เพียง 1 คน
    const topByBroker = {};
    customers
      .filter((c) => bKeys.has(c.broker_key))
      .map((c) => ({ ...c, volume: sideAmt(c.deposit, c.withdrawal) }))
      .filter((c) => c.volume > 0)
      .forEach((c) => {
        if (!topByBroker[c.broker_key] || c.volume > topByBroker[c.broker_key].volume) topByBroker[c.broker_key] = c;
      });
    const custs = bs.map((b) => topByBroker[b.key]).filter(Boolean);

    // 3) gateway ทุกตัวที่เปิดใช้งาน + ยอดที่ broker ที่แสดงส่งผ่านมัน (ตัวที่ไม่มี broker ตั้งค่า = idle)
    const gwAgg = {};
    bs.forEach((b) => {
      if (b.gateway_deposits) {
        gwAgg[b.gateway_deposits] = gwAgg[b.gateway_deposits] || { deposit: 0, withdrawal: 0 };
        gwAgg[b.gateway_deposits].deposit += b.deposit;
      }
      if (b.gateway_withdrawals) {
        gwAgg[b.gateway_withdrawals] = gwAgg[b.gateway_withdrawals] || { deposit: 0, withdrawal: 0 };
        gwAgg[b.gateway_withdrawals].withdrawal += b.withdrawal;
      }
    });
    const gwList = [...gateways];
    Object.keys(gwAgg).forEach((k) => {
      if (!gwList.some((g) => g.key === k)) gwList.push({ key: k, name: titleCase(k), code: k.toUpperCase(), is_deposit_active: true, is_withdrawal_active: true });
    });
    const gws = gwList
      .map((g) => { const v = gwAgg[g.key] || { deposit: 0, withdrawal: 0 }; return { ...g, ...v, volume: sideAmt(v.deposit, v.withdrawal) }; })
      .sort((a, b) => b.volume - a.volume || a.name.localeCompare(b.name));

    // 4) bank ทุกตัวที่เปิดใช้งาน + ยอดของ customer ที่ใช้ธนาคารนั้น (ไม่มีลูกค้า = idle)
    const bankAgg = {};
    custs.forEach((c) => {
      const code = c.bank_code || "OTHER";
      bankAgg[code] = bankAgg[code] || { deposit: 0, withdrawal: 0 };
      bankAgg[code].deposit += c.deposit;
      bankAgg[code].withdrawal += c.withdrawal;
    });
    const bankList = [...banks];
    Object.keys(bankAgg).forEach((k) => {
      if (!bankList.some((b) => b.key === k)) bankList.push({ key: k, code: k, name: bankOf(k)?.label || (k === "OTHER" ? "Other bank" : k), gateways: [] });
    });
    const bankNodes = bankList
      .map((b) => { const v = bankAgg[b.key] || { deposit: 0, withdrawal: 0 }; return { ...b, full_name: b.name || b.code, name: b.code, ...v, volume: sideAmt(v.deposit, v.withdrawal) }; })
      .sort((a, b) => b.volume - a.volume || a.code.localeCompare(b.code));

    // ---------- geometry ----------
    const needed = Math.max(custs.length * ROW_GAP, bankNodes.length * ROW_GAP, bs.length * STACK_GAP, gws.length * STACK_GAP, ROW_GAP);
    const height = Math.max(MIN_H, 160 + needed);
    const hubPos = { x: COL_X.hub, y: height / 2 };

    const nodes = {};
    let colorIdx = 0;
    const addNode = (type, item, i, count, x) => {
      const id = `${type}:${item.key}`;
      nodes[id] = { ...item, id, type, x, y: spreadY(count, i, height), color: FLOW_COLORS[colorIdx++ % FLOW_COLORS.length], customers: new Set(), order: i, count };
      return nodes[id];
    };
    custs.forEach((c, i) => addNode("customer", c, i, custs.length, COL_X.customer));
    bs.forEach((b, i) => addNode("broker", b, i, bs.length, COL_X.broker));
    gws.forEach((g, i) => addNode("gateway", g, i, gws.length, COL_X.gateway));
    bankNodes.forEach((b, i) => addNode("bank", b, i, bankNodes.length, COL_X.bank));
    // customer อยู่แถวเดียวกับ broker ของตัวเอง เส้น customer -> broker จะเป็นเส้นตรง ไม่ไขว้กัน
    custs.forEach((c) => { const bN = nodes[`broker:${c.broker_key}`]; if (bN) nodes[`customer:${c.key}`].y = bN.y; });
    const hub = { id: "hub", type: "hub", x: hubPos.x, y: hubPos.y, customers: new Set(custs.map((c) => c.key)) };

    const edges = {};
    const addEdge = (from, to, side, amount, customerKey, color) => {
      if (!from || !to || amount <= 0) return;
      const id = `${from.id}->${to.id}`;
      if (!edges[id]) edges[id] = { id, from, to, deposit: 0, withdrawal: 0, customers: new Set(), color };
      edges[id][side] += amount;
      edges[id].customers.add(customerKey);
      from.customers.add(customerKey);
      to.customers.add(customerKey);
    };
    custs.forEach((c) => {
      const cN = nodes[`customer:${c.key}`];
      const bN = nodes[`broker:${c.broker_key}`];
      const bk = brokerMap[c.broker_key];
      const gDep = nodes[`gateway:${bk.gateway_deposits}`] || null;
      const gWd = nodes[`gateway:${bk.gateway_withdrawals}`] || null;
      const bankN = nodes[`bank:${c.bank_code || "OTHER"}`] || null;
      // deposit: customer -> broker -> hub -> gateway(dep) -> bank
      addEdge(cN, bN, "deposit", c.deposit, c.key, cN.color);
      addEdge(bN, hub, "deposit", c.deposit, c.key, bN.color);
      addEdge(hub, gDep, "deposit", c.deposit, c.key, gDep?.color);
      addEdge(gDep, bankN, "deposit", c.deposit, c.key, gDep?.color);
      // withdrawal: วิ่งย้อนบนเส้นเดียวกัน แต่ผ่าน gateway ฝั่งถอน
      addEdge(cN, bN, "withdrawal", c.withdrawal, c.key, cN.color);
      addEdge(bN, hub, "withdrawal", c.withdrawal, c.key, bN.color);
      addEdge(hub, gWd, "withdrawal", c.withdrawal, c.key, gWd?.color);
      addEdge(gWd, bankN, "withdrawal", c.withdrawal, c.key, gWd?.color);
    });

    const edgeList = Object.values(edges).map((e) => ({ ...e, volume: sideAmt(e.deposit, e.withdrawal) }));
    const maxEdge = Math.max(1, ...edgeList.map((e) => e.volume));
    edgeList.forEach((e) => {
      const { from, to } = e;
      const x1 = from.x + (from.type === "hub" ? 46 : NODE_R);
      const x2 = to.x - (to.type === "hub" ? 46 : NODE_R);
      // เส้นที่อยู่กลางคอลัมน์เลี้ยวใกล้ปลายทางที่สุด เส้นขอบเลี้ยวไกลออกไป (สลับระยะไม่ทับกัน)
      const src = from.type === "hub" ? to : from;
      const order = Math.abs(src.order - (src.count - 1) / 2);
      const mid = (x1 + x2) / 2;
      const bendX = from.type === "hub" ? x1 + 70 + order * 16 : to.type === "hub" ? x2 - 70 - order * 16 : mid + (src.order % 2 ? 1 : -1) * order * 10;
      e.path = elbowPath(x1, from.y, x2, to.y, bendX);
      e.bend = { x: bendX, y: from.type === "hub" ? from.y : to.type === "hub" ? to.y : from.y };
      e.weight = e.volume / maxEdge;
    });

    const nodeList = Object.values(nodes);
    nodeList.forEach((n) => {
      n.idle = n.customers.size === 0;
      if (n.idle) n.color = IDLE_COLOR;
    });

    // เส้น gateway -> bank จาก mapping (ใช้งานได้) เฉพาะคู่ที่ยังไม่มี flow edge
    // live = ธนาคารปลายทางมียอดแล้ว -> ใส่ effect เต็ม (glow/อนุภาค) ถ้ายังไม่มีเป็นเส้นบางนิ่ง ๆ
    bankNodes.forEach((b) => {
      const to = nodes[`bank:${b.key}`];
      (b.gateways || []).forEach((g) => {
        const from = nodes[`gateway:${g}`];
        if (!from || edges[`${from.id}->${to.id}`]) return;
        const x1 = from.x + NODE_R, x2 = to.x - NODE_R;
        const order = Math.abs(to.order - (to.count - 1) / 2);
        const bendX = (x1 + x2) / 2 + (to.order % 2 ? 1 : -1) * order * 8;
        const color = !from.idle ? from.color : !to.idle ? to.color : LINK_COLOR;
        edgeList.push({
          id: `${from.id}->${to.id}`, from, to, link: true, live: !to.idle, color,
          deposit: 0, withdrawal: 0, volume: 0, weight: 0.1, customers: new Set(),
          is_deposit_active: from.is_deposit_active !== false, is_withdrawal_active: from.is_withdrawal_active !== false,
          path: elbowPath(x1, from.y, x2, to.y, bendX), bend: { x: bendX, y: from.y },
        });
      });
    });
    edgeList.forEach((e, i) => (e.svgId = `fm-e-${i}`));

    // ---------- แผนอนุภาค: จำกัดจำนวนรวมทั้งภาพ ----------
    // เส้นที่มียอดจริงได้ก่อน (เรียงตามน้ำหนัก) เส้น mapping ที่ live ได้ทีหลังถ้ายังมีงบ
    const sideOk = (side) => flow_side === "all" || flow_side === side;
    const wants = [];
    edgeList.forEach((e) => {
      e.particles = { deposit: 0, withdrawal: 0 };
      ["deposit", "withdrawal"].forEach((side) => {
        if (!sideOk(side)) return;
        if (e.link) {
          if (!e.live) return;
          if (side === "deposit" ? !e.is_deposit_active : !e.is_withdrawal_active) return;
          wants.push({ e, side, count: 1, w: 0.1, dur: 7, priority: 0 });
        } else {
          if (e[side] <= 0) return;
          const w = Math.max(0.08, Math.min(1, e[side] / Math.max(1, e.volume)) * e.weight);
          wants.push({ e, side, count: Math.max(1, Math.round(1 + w * 2)), w, dur: 5.5 - w * 3, priority: 1 + w });
        }
      });
    });
    wants.sort((a, b) => b.priority - a.priority);
    let budget = MAX_PARTICLES;
    // รอบแรกให้ทุกเส้น 1 เม็ดตามลำดับความสำคัญ รอบสองค่อยเติมเม็ดที่เหลือของเส้นหนัก
    wants.forEach((p) => { if (budget > 0) { p.e.particles[p.side] = 1; p.e.particles[`${p.side}_dur`] = p.dur; budget--; } });
    wants.forEach((p) => { const extra = Math.min(p.count - 1, budget); if (extra > 0) { p.e.particles[p.side] += extra; budget -= extra; } });

    return { nodes: nodeList, hub, edges: edgeList, custs, height };
  };

  // ---------- particles (ไม่ใช้ filter: วงนอกโปร่งแสงเป็นแสงฟุ้ง + วงในทึบเป็นแกน) ----------
  Particles = (edge, side) => {
    if (!this.state.animate) return null;
    const count = edge.particles?.[side] || 0;
    if (!count) return null;
    const isDeposit = side === "deposit";
    const dur = edge.particles[`${side}_dur`] || 5;
    const color = isDeposit ? DEPOSIT_PARTICLE : WITHDRAWAL_PARTICLE;
    return Array.from({ length: count }, (_, k) => (
      <g key={`${edge.id}-${side}-${k}`} className="fm-particle">
        <circle r={isDeposit ? 7 : 6} fill={color} opacity={0.28} />
        <circle r={isDeposit ? 3.2 : 2.6} fill={color} />
        <animateMotion
          dur={`${dur.toFixed(2)}s`}
          repeatCount="indefinite"
          begin={`${(-(dur * k) / count).toFixed(2)}s`}
          calcMode="linear"
          keyPoints={isDeposit ? "0;1" : "1;0"}
          keyTimes="0;1"
        >
          <mpath href={`#${edge.svgId}`} />
        </animateMotion>
      </g>
    ));
  };

  // ---------- interaction ----------
  // ย้าย tooltip ผ่าน DOM โดยตรง: เมาส์ขยับไม่ต้อง re-render SVG ทั้งภาพ
  MoveTooltip = (evt) => {
    const stage = this.stageRef.current;
    const tip = this.tooltipRef.current;
    if (!stage || !tip) return;
    const rect = stage.getBoundingClientRect();
    tip.style.left = `${Math.min(rect.width - 250, Math.max(8, evt.clientX - rect.left + 14))}px`;
    tip.style.top = `${Math.max(8, evt.clientY - rect.top - 10)}px`;
  };
  Enter = (node, evt) => {
    this.setState({ hover: node.id, tooltip_node: node }, () => this.MoveTooltip(evt));
  };
  Leave = () => {
    this.setState({ hover: null, tooltip_node: null });
  };

  render() {
    const { filter_currency, flow_side, is_sample, loaded, hover, pinned, tooltip_node, animate } = this.state;
    const currency = global.currency?.find((c) => c.currency_code === filter_currency);
    const currencySymbol = filter_currency === "THB" ? "฿" : filter_currency === "LAK" ? "₭" : "";

    const { nodes, hub, edges, custs, height } = this.Graph();
    const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
    byId.hub = hub;
    const glowHexes = buildGlowHexes(height);

    // customer set ที่โฟกัส: จาก pinned ก่อน แล้ว hover
    const focusId = pinned || hover;
    const focusCustomers = focusId && focusId !== "hub" ? byId[focusId]?.customers : null;
    const intersects = (set) => {
      if (!focusCustomers) return true;
      for (const k of set) if (focusCustomers.has(k)) return true;
      return false;
    };

    const totalDeposit = custs.reduce((s, c) => s + c.deposit, 0);
    const totalWithdrawal = custs.reduce((s, c) => s + c.withdrawal, 0);
    const activeCount = nodes.filter((n) => !n.idle).length;
    const idleCount = nodes.length - activeCount;

    const strokeW = (w) => 1.2 + w * 3.2;

    const renderEdge = (e) => {
      const active = e.link
        ? !focusCustomers || (intersects(e.from.customers) && intersects(e.to.customers))
        : intersects(e.customers);
      const quiet = e.link && !e.live; // เส้นทางใช้ได้แต่ธนาคารยังไม่มียอด: ไม่มี glow/อนุภาค
      return (
        <g key={e.id} className={`fm-edge ${active ? "" : "fm-dim"}`}>
          <path id={e.svgId} d={e.path} fill="none" stroke={e.color} strokeWidth={strokeW(e.weight)} strokeOpacity={quiet ? 0.28 : 0.55} strokeLinecap="round" />
          {!quiet && (
            <>
              <path d={e.path} fill="none" stroke={e.color} strokeWidth={strokeW(e.weight) + 6} strokeOpacity={0.12} strokeLinecap="round" filter="url(#fm-glow)" />
              <circle cx={e.bend.x} cy={e.bend.y} r={3} fill={e.color} filter="url(#fm-glow)" />
              {this.Particles(e, "deposit")}
              {this.Particles(e, "withdrawal")}
            </>
          )}
        </g>
      );
    };

    const renderNode = (n) => {
      const active = intersects(n.customers);
      const isPinned = pinned === n.id;
      const logo = n.type === "gateway" ? GATEWAY_LOGO[n.key] : n.type === "bank" ? bankIcon(n.code) : null;
      const logoBg = n.type === "gateway" ? gateway_logo_background(n.key) : "#ffffff";
      // customer: label ซ้ายโหนด | bank: label ขวาโหนด | broker/gateway: label บน-ล่างโหนด (คอลัมน์กลาง)
      const sideLabel = n.type === "customer" ? -1 : n.type === "bank" ? 1 : 0;
      const amountLabel = n.idle ? "idle" : `${currencySymbol}${compact(flow_side === "withdrawal" ? n.withdrawal : n.deposit)}`;
      const subText = n.type === "customer" ? "CUSTOMER" : n.type === "broker" ? n.code || "BROKER" : n.type === "bank" ? "" : n.code;
      const nameText = n.type === "customer" ? ellipsis(n.name, 14) : n.name;
      const words = String(n.name || "?").trim().split(/\s+/);
      // ชื่อคำเดียวใช้ 2 ตัวอักษรแรก (Spark -> SP, Scb -> SC) ไม่ให้ซ้ำกัน
      const initials = (words.length > 1 ? words.map((w) => w[0]).join("") : words[0]).slice(0, 2).toUpperCase();
      return (
        <g
          key={n.id}
          className={`fm-node ${active ? "" : "fm-dim"} ${isPinned ? "fm-pinned" : ""} ${n.idle ? "fm-idle" : ""}`}
          onMouseEnter={(e) => this.Enter(n, e)}
          onMouseMove={this.MoveTooltip}
          onMouseLeave={this.Leave}
          onClick={() => this.setState({ pinned: isPinned ? null : n.id })}
        >
          {animate && active && !n.idle && <circle cx={n.x} cy={n.y} r={NODE_R} className="fm-pulse" stroke={n.color} />}
          <circle
            cx={n.x} cy={n.y} r={NODE_R}
            fill="#0b1220" stroke={n.color} strokeWidth={n.idle ? 1.2 : 2}
            strokeDasharray={n.idle ? "4 4" : undefined}
            filter={n.idle ? undefined : "url(#fm-node-glow)"}
          />
          {logo ? (
            <>
              <circle cx={n.x} cy={n.y} r={17} fill={logoBg} opacity={n.idle ? 0.55 : 1} />
              <g transform={`translate(${n.x - 17}, ${n.y - 17})`} opacity={n.idle ? 0.55 : 1}>
                <image href={logo} x={0} y={0} width={34} height={34} clipPath="url(#fm-clip-logo)" preserveAspectRatio="xMidYMid slice" />
              </g>
            </>
          ) : (
            <text x={n.x} y={n.y + 5} textAnchor="middle" className="fm-node-initial" fill={n.color}>
              {initials}
            </text>
          )}
          {sideLabel !== 0 ? (
            <>
              <text x={n.x + sideLabel * 34} y={n.y - 4} textAnchor={sideLabel < 0 ? "end" : "start"} className="fm-node-name">{nameText}</text>
              <text x={n.x + sideLabel * 34} y={n.y + 14} textAnchor={sideLabel < 0 ? "end" : "start"} className="fm-node-sub">
                {subText}
                <tspan className="fm-node-amount" dx={subText ? 8 : 0}>{amountLabel}</tspan>
              </text>
            </>
          ) : (
            <>
              <text x={n.x} y={n.y - NODE_R - 8} textAnchor="middle" className="fm-node-name">{n.name}</text>
              <text x={n.x} y={n.y + NODE_R + 16} textAnchor="middle" className="fm-node-sub">
                {subText}
                <tspan className="fm-node-amount" dx="6">{amountLabel}</tspan>
              </text>
            </>
          )}
        </g>
      );
    };

    const t = tooltip_node;
    const tooltipRows = t
      ? [
          t.idle && ["Status", "Enabled · no flow yet"],
          ["Deposit", `${currencySymbol}${toFixed(t.deposit, 2)}`],
          ["Withdrawal", `${currencySymbol}${toFixed(t.withdrawal, 2)}`],
          t.type === "customer" && ["Broker", byId[`broker:${t.broker_key}`]?.name || "-"],
          t.type === "customer" && ["Bank", bankOf(t.bank_code)?.label || t.bank_code || "-"],
          t.type === "broker" && ["Deposit via", titleCase(t.gateway_deposits) || "-"],
          t.type === "broker" && ["Withdraw via", titleCase(t.gateway_withdrawals) || "-"],
          t.type === "bank" && ["Bank", t.full_name || t.code],
          t.type === "bank" && ["Usable via", (t.gateways || []).map(titleCase).join(", ") || "-"],
          t.type === "gateway" && ["Rule", [t.is_deposit_active && "Deposit", t.is_withdrawal_active && "Withdrawal"].filter(Boolean).join(" + ") || "-"],
          ["Customers on path", String(t.customers.size)],
        ].filter(Boolean)
      : [];

    return (
      <div className="w-100 min-vh-100 premium-report">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* ===== Hero ===== */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Flow Map</div>
                <div className="ar-title">Money Flow Map</div>
              </div>
              <div className="ar-hero-actions">
                <button className="ar-btn-ghost" onClick={() => this.setState({ animate: !animate })}>
                  <span className="icon me-2">{animate ? "" : ""}</span>
                  {animate ? "Pause" : "Play"}
                </button>
              <div className="text-end">
                <div className="ar-year-caption">
                  <span className="ar-caption-chip ms-2">{activeCount} active · {idleCount} idle · {edges.filter((e) => !e.link).length} routes</span>
                </div>
              </div>
              </div>
            </div>

            <div className="ar-toolbar ar-filter mt-3">
<div className="ar-filter-head">
<span className="ar-toolbar-label"><span className="icon me-2">{"\uf0b0"}</span>Filter</span>
</div>
<div className="ar-filter-row">
<div className="ar-filter-field ar-filter-field-auto">
<span className="ar-filter-label">Flow</span>
<div className="d-flex align-items-center gap-2">
                  {[
                    { value: "all", label: "Both" },
                    { value: "deposit", label: "Deposit" },
                    { value: "withdrawal", label: "Withdrawal" },
                  ].map((s) => (
                    <button
                      key={s.value}
                      className={`ar-pill ${flow_side === s.value ? "active" : ""}`}
                      onClick={() => this.setState({ flow_side: s.value, pinned: null, hover: null, tooltip_node: null })}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
</div>
</div>
<div className="ar-filter-foot">
<div className="d-flex align-items-center gap-2">
                  {global.currency?.map((item) => (
                    <button
                      key={item.currency_code}
                      className={`ar-pill ${item.currency_code === filter_currency ? "active" : ""}`}
                      onClick={() => this.setState({ filter_currency: item.currency_code }, () => this.Get())}
                    >
                      {item.image}
                      <span className="ms-1">{item.currency_code}</span>
                    </button>
                  ))}
                </div>
</div>
</div>
          </div>

          {/* ===== Stage ===== */}
          <div className="ar-table-card mb-4">
            <div className="ar-table-head flex-wrap gap-2">
              <div>
                <div className="ar-table-title">Flow Network</div>
                <div className="ar-table-caption">
                  Every enabled gateway and bank is shown · flow runs only where a broker's gateway setting or a customer's bank sends it · hover to trace, click to pin
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 fm-legend">
                <span><i style={{ background: DEPOSIT_PARTICLE, color: DEPOSIT_PARTICLE }} /> Deposit →</span>
                <span><i style={{ background: WITHDRAWAL_PARTICLE, color: WITHDRAWAL_PARTICLE }} /> ← Withdrawal</span>
                <span><i className="fm-legend-line" /> Volume</span>
                <span><i className="fm-legend-idle" /> Enabled, no flow</span>
                <span><i className="fm-legend-link" /> Usable route (animates once the bank has volume)</span>
                {is_sample && <span className="fm-sample-badge">Sample data</span>}
              </div>
            </div>

            <div className="fm-stage-wrap">
              <div className="fm-stage" ref={this.stageRef} onClick={(e) => { if (e.target === e.currentTarget) this.setState({ pinned: null }); }}>
                <svg viewBox={`0 0 ${VB_W} ${height}`} className="fm-svg" role="img" aria-label="Money flow network">
                  <defs>
                    <pattern id="fm-hex" width={HEX_DX} height={HEX_DY * 2} patternUnits="userSpaceOnUse">
                      <polygon points={hexPoints(HEX_DX / 2, 0, HEX_R - 1.5)} className="fm-hex-cell" />
                      <polygon points={hexPoints(HEX_DX / 2, HEX_DY * 2, HEX_R - 1.5)} className="fm-hex-cell" />
                      <polygon points={hexPoints(0, HEX_DY, HEX_R - 1.5)} className="fm-hex-cell" />
                      <polygon points={hexPoints(HEX_DX, HEX_DY, HEX_R - 1.5)} className="fm-hex-cell" />
                    </pattern>
                    <radialGradient id="fm-vignette" cx="50%" cy="50%" r="70%">
                      <stop offset="0%" stopColor="#131f3a" />
                      <stop offset="100%" stopColor="#070c18" />
                    </radialGradient>
                    <radialGradient id="fm-hub-grad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#fff3c4" />
                      <stop offset="55%" stopColor={HUB_COLOR} />
                      <stop offset="100%" stopColor="#7a5b12" />
                    </radialGradient>
                    <filter id="fm-glow" x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur stdDeviation="2.2" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <filter id="fm-node-glow" x="-60%" y="-60%" width="220%" height="220%">
                      <feGaussianBlur stdDeviation="4" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <filter id="fm-hub-glow" x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur stdDeviation="12" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <clipPath id="fm-clip-logo">
                      <circle cx={17} cy={17} r={17} />
                    </clipPath>
                  </defs>

                  <rect width={VB_W} height={height} fill="url(#fm-vignette)" />
                  <rect width={VB_W} height={height} fill="url(#fm-hex)" />
                  {animate &&
                    glowHexes.map((h) => (
                      <polygon
                        key={h.id}
                        points={h.points}
                        fill={h.color}
                        className="fm-hex-glow"
                        style={{ "--fm-a": h.alpha, animationDelay: `${h.delay}s`, animationDuration: `${h.dur}s` }}
                      />
                    ))}

                  {edges.map(renderEdge)}

                  {/* HUB */}
                  <g
                    className="fm-hub"
                    onMouseEnter={() => this.setState({ hover: "hub" })}
                    onMouseLeave={() => this.setState({ hover: null })}
                  >
                    {animate && (
                      <>
                        <polygon points={hexPoints(hub.x, hub.y, 44)} className="fm-hub-ring" stroke={HUB_COLOR} />
                        <polygon points={hexPoints(hub.x, hub.y, 44)} className="fm-hub-ring fm-hub-ring-2" stroke={HUB_COLOR} />
                      </>
                    )}
                    <polygon points={hexPoints(hub.x, hub.y, 58)} fill="none" stroke={HUB_COLOR} strokeOpacity={0.35} strokeDasharray="6 8" className={animate ? "fm-hub-dash" : ""} />
                    <polygon points={hexPoints(hub.x, hub.y, 42)} fill="url(#fm-hub-grad)" filter="url(#fm-hub-glow)" />
                    <polygon points={hexPoints(hub.x, hub.y, 42)} fill="none" stroke="#fff7d6" strokeOpacity={0.7} strokeWidth={1.5} />
                    <text x={hub.x} y={hub.y - 2} textAnchor="middle" className="fm-hub-title">ZENITH</text>
                    <text x={hub.x} y={hub.y + 14} textAnchor="middle" className="fm-hub-sub">LINK HUB</text>
                    <text x={hub.x} y={hub.y + 84} textAnchor="middle" className="fm-hub-caption">
                      {currencySymbol}{compact(totalDeposit + totalWithdrawal)} · {custs.length} customers
                    </text>
                  </g>

                  {nodes.map(renderNode)}

                  {/* หัวคอลัมน์ */}
                  <text x={COL_X.customer} y={36} textAnchor="middle" className="fm-col-title">CUSTOMERS</text>
                  <text x={COL_X.broker} y={36} textAnchor="middle" className="fm-col-title">BROKERS</text>
                  <text x={COL_X.hub} y={36} textAnchor="middle" className="fm-col-title">ZENITHLINK</text>
                  <text x={COL_X.gateway} y={36} textAnchor="middle" className="fm-col-title">GATEWAYS</text>
                  <text x={COL_X.bank} y={36} textAnchor="middle" className="fm-col-title">BANKS</text>

                  {loaded && nodes.length === 0 && (
                    <text x={VB_W / 2} y={height / 2} textAnchor="middle" className="fm-empty">No flow to display</text>
                  )}
                </svg>

                {t && (
                  <div className="fm-tooltip" ref={this.tooltipRef} style={{ left: 8, top: 8, "--fm-c": t.color }}>
                    <div className="fm-tooltip-title">
                      <i />
                      {t.name}
                      <span className="fm-tooltip-tag">{t.type.toUpperCase()}</span>
                    </div>
                    {tooltipRows.map(([k, v]) => (
                      <div className="fm-tooltip-row" key={k}>
                        <span>{k}</span>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
